import i from 'i18next'
import { useTransform, motion } from 'framer-motion'
import useMedia from 'use-media'
import Link from 'next/link'
import { MenuCollection } from 'lib/hooks/use-collections'
import { useYpos } from 'lib/hooks/use-ypos'
import Image from 'components/Image'
import { getTitle } from 'components/collection/Title'
import { useScrollerMotion } from 'scroller-motion'

type Props = {
	collection: MenuCollection
}

export default function Collection({ collection }: Props ) {
	const mobile = useMedia({ maxWidth: '900px' })

	const { scrollY } = useScrollerMotion()
	const { ref, start, stop } = useYpos()
	const transform = ( mobile ? [ '0', '0' ] : [ '30%', '-30%' ] )
	const y = useTransform( scrollY, [ start, stop ], transform )

	if ( !collection.image || !collection.bigImage ) return null

	const title = i.t( [
		`collection.title.${collection.handle}`,
		'collection.title.generic',
	], { collection: getTitle( collection ) })

	const isChrome = (
		typeof window !== 'undefined' &&
		// @ts-ignore
		( !!window?.chrome?.webstore || !!window?.chrome?.runtime )
	)

	return (
		<section ref={ref}>
			<picture>
				<Image
					src={collection.bigImage.originalSrc}
					alt={collection.bigImage.altText}
					loading="lazy"
					sizes="100vh"
				/>
			</picture>
			<motion.div
				className={( !mobile ? 'container' : undefined )}
				style={{ flex: 1, y }}
			>
				<Link
					href="/products/[collection]"
					as={`/products/${collection.handle}`}
					scroll={false}
					legacyBehavior>
					<a className="u-a" aria-label={title}>
						<article>
							<picture>
								<Image
									src={collection.image.originalSrc}
									alt={collection.image.altText}
									loading="lazy"
									sizes={`(max-width: 900px) 100vw, ${Math.ceil( 350 * 1.05 )}px`}
									whileHover={( !isChrome && { scale: 1.05 })}
								/>
							</picture>
							<div className={( mobile && 'container' )}>
								<h2>{title}</h2>
							</div>
						</article>
					</a>
				</Link>
			</motion.div>
			<style jsx>{`
				section {
					display: flex;
					align-items: center;
				}
				section > picture {
					width: 50%;
					height: 125vh;
				}
				a {
					display: block;
					max-width: 350px;
					margin: auto;
					text-decoration: none;
				}
				article h2 {
					margin-top: 1em;
					font-size: 20px;
  				line-height: 1.45;
				}
				article picture {
					height: 520px;
					max-height: 60vh;
				}
				@media ( max-height: 800px ) {
					a {
						max-width: 40vh;
					}
				}
				@media ( max-width: 900px ) {
					section > picture {
						display: none;
					}
				}
				@media ( max-width: 500px ) {
					a {
						max-width: 100%;
					}
					article picture {
						height: 60vh;
					}
				}
			`}</style>
		</section>
	)
}
